import { forwardRef, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { Button, Icon, Menu, TextButton } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconClockFilled, IconExternalOutline } from '@zillow/constellation-icons';
import { LoadingButton } from '@zg-rentals/loading-components';

import { useSelector } from '../../selectors/useSelector';

// utils
import propertyUtils from '../../utils/propertyUtils';

// constants
import PropertyConstants from '../../constants/PropertyConstants';
import StringConstants from '../../constants/StringConstants';

import { singleTextLineStyles } from '../../styles';

import type { Ref } from 'react';
import type { TextButtonPropsInterface } from '@zillow/constellation-10';
import type { SyndicationProps } from '../../model/Syndication';

const DISPLAY_TYPE = {
  DROPDOWN_BUTTON: 'DROPDOWN_BUTTON',
  TEXT_BUTTON: 'TEXT_BUTTON',
} as const;

const origSyndicationByBrandSF = [
  StringConstants.UNFORMATTED_BRAND_NAMES.zillow,
  StringConstants.UNFORMATTED_BRAND_NAMES.trulia,
  StringConstants.UNFORMATTED_BRAND_NAMES.hotpads,
] as const;

const externalSyndicationBrandMF_MAP: Record<string, string> = {
  [StringConstants.UNFORMATTED_BRAND_NAMES.realtor]: StringConstants.UNFORMATTED_BRAND_NAMES.realtor,
  [StringConstants.UNFORMATTED_BRAND_NAMES.rent]: StringConstants.UNFORMATTED_BRAND_NAMES.rent,
  [StringConstants.UNFORMATTED_BRAND_NAMES.redfin]: StringConstants.UNFORMATTED_BRAND_NAMES.redfin,
  [StringConstants.UNFORMATTED_BRAND_NAMES.apartmentGuide]: StringConstants.UNFORMATTED_BRAND_NAMES.apartmentGuide,
};

const origSyndicationByBrandMF = [
  StringConstants.UNFORMATTED_BRAND_NAMES.zillow,
  StringConstants.UNFORMATTED_BRAND_NAMES.trulia,
  StringConstants.UNFORMATTED_BRAND_NAMES.hotpads,

  // external
  StringConstants.UNFORMATTED_BRAND_NAMES.realtor,
  StringConstants.UNFORMATTED_BRAND_NAMES.rent,
  StringConstants.UNFORMATTED_BRAND_NAMES.redfin,
  StringConstants.UNFORMATTED_BRAND_NAMES.apartmentGuide,
] as const;

type Props = {
  label: string | JSX.Element | null;
  syndication: SyndicationProps;
  displayType?: string;
  useFixedWidth?: boolean;
  size?: TextButtonPropsInterface['size'];
  status?: string;
  onMenuOpen?: () => void;
  isLoading?: boolean;
};

const ConnectedNetworkView = forwardRef((props: Props, ref) => {
  const {
    label,
    syndication,
    displayType = DISPLAY_TYPE.TEXT_BUTTON,
    useFixedWidth = false,
    size = 'lg',
    status = null,
    onMenuOpen = null,
    isLoading = false,
  } = props;

  const { currentProperty, isMultifamily } = useSelector(
    (state) => ({
      currentProperty: state?.properties?.currentProperty,
      isMultifamily: state?.properties?.currentProperty?.isMultifamily,
    }),
    shallowEqual,
  );

  const syndicationByBrand = isMultifamily ? origSyndicationByBrandMF : origSyndicationByBrandSF;

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const handleMenuOpen = () => {
    if (onMenuOpen) {
      onMenuOpen();
    }
  };

  const getDisplayStatusPrefix = useMemo(
    () => (brand: (typeof origSyndicationByBrandSF)[number] | (typeof origSyndicationByBrandMF)[number]) => {
      let displayStatusPrefix = 'View on';

      if (!isRoomForRent) {
        return displayStatusPrefix;
      }

      if (StringConstants.FORMATTED_BRAND_NAMES[brand] === StringConstants.FORMATTED_BRAND_NAMES.trulia) {
        displayStatusPrefix = 'Not available on';
      }
      return displayStatusPrefix;
    },
    [isRoomForRent],
  );

  return (
    <Menu.Root onOpenChange={(open) => open && handleMenuOpen()}>
      <Menu.Trigger>
        {displayType === DISPLAY_TYPE.DROPDOWN_BUTTON ? (
          isLoading ? (
            <LoadingButton isLoading={isLoading} loadingWidth={size === 'sm' ? '50%' : '100%'}>
              &nbsp;
            </LoadingButton>
          ) : (
            <Button.Root
              data-testid="ViewYourListingDropdownButton"
              tone="neutral"
              emphasis="outlined"
              size={size}
              fluid={true}
              title={label as string}
              css={{
                width: useFixedWidth ? { lg: '300px' } : undefined,
              }}
            >
              <Button.Label css={{ ...singleTextLineStyles, fontSize: 'body' }}>{label}</Button.Label>
              <Button.Icon render={<IconChevronDownFilled />} />
            </Button.Root>
          )
        ) : (
          <TextButton.Root data-testid="ConnectedNetworkView-TextButton" title={label as string}>
            <TextButton.Label
              css={{
                fontSize: { base: 'body-xs', lg: 'body-sm', xl: 'body' },
                fontWeight: { base: 'regular', lg: '600' },
              }}
            >
              {label}
            </TextButton.Label>
          </TextButton.Root>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content data-testid="ConnectedNetworkView" ref={ref as Ref<HTMLDivElement>}>
          {displayType === DISPLAY_TYPE.DROPDOWN_BUTTON &&
            status?.toLowerCase() === PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PUBLISHING && (
              <Menu.Heading level={3}>
                <Icon render={<IconClockFilled />} /> It may take up to 1 hour for your listing to post
              </Menu.Heading>
            )}

          {syndicationByBrand.map((brand) => {
            const displayBrand = StringConstants.FORMATTED_BRAND_NAMES[brand];
            const displayStatusPrefix = getDisplayStatusPrefix(brand);

            if (syndication?.[brand]?.url) {
              return (
                <Menu.Item key={`menu-item-${brand}`} asChild>
                  <a target="_blank" rel="noreferrer" href={syndication[brand].url}>
                    <Menu.ItemIcon render={<IconExternalOutline />} />
                    <Menu.ItemLabel>{`${displayStatusPrefix} ${displayBrand}`}</Menu.ItemLabel>
                  </a>
                </Menu.Item>
              );
            }

            if (externalSyndicationBrandMF_MAP[brand]) {
              return null;
            }

            return (
              <Menu.Item disabled={true} key={`menu-item-${brand}`}>
                <Menu.ItemIcon render={<IconExternalOutline />} />
                <Menu.ItemLabel>{`${displayStatusPrefix} ${displayBrand}`}</Menu.ItemLabel>
              </Menu.Item>
            );
          })}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
});

ConnectedNetworkView.displayName = 'ConnectedNetworkView';

export { DISPLAY_TYPE };

export default ConnectedNetworkView;
